import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getReportsByOrg, getOrganisations, duplicateReport, updateReportStatus, exportReportListExcel } from '../api';
import EmptyState from '../components/EmptyState';
import Breadcrumb from '../components/Breadcrumb';

const COLUMNS = [
  { key: 'organisationName', label: 'Organisation', sortable: true },
  { key: 'siteName', label: 'Site', sortable: true },
  { key: 'assessmentDate', label: 'Date', sortable: true },
  { key: 'assessorName', label: 'Assessor', sortable: true },
  { key: 'versionNumber', label: 'Version', sortable: true },
  { key: 'systemVersion', label: 'Rev', sortable: true },
  { key: 'updatedAt', label: 'Last Modified', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
];

const PAGE_SIZE = 50;

const STATUS_OPTIONS = ['', 'DRAFT', 'IN_PROGRESS', 'REVIEW', 'ASSESSOR_COMPLETED', 'QA_APPROVED'];
const STATUS_LABELS = { '': 'All Statuses', DRAFT: 'Draft', IN_PROGRESS: 'In Progress', REVIEW: 'Review', ASSESSOR_COMPLETED: 'Assessor Completed', QA_APPROVED: 'QA Approved' };

export default function ReportsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allReports, setAllReports] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState(new Set());
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicating, setDuplicating] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);

  // Sorting — default: latest first
  const [sortBy, setSortBy] = useState('assessmentDate');
  const [sortDir, setSortDir] = useState('desc');

  // Load orgs then all reports
  useEffect(() => {
    setLoading(true);
    getOrganisations()
      .then((orgList) => {
        setOrgs(orgList);
        // Select all orgs by default
        setSelectedOrgIds(new Set(orgList.map((o) => o.id)));
        // Fetch reports from all orgs in parallel
        return Promise.all(
          orgList.map((o) =>
            getReportsByOrg(o.id, { page: 0, size: 10000, sortBy: 'assessmentDate', sortDir: 'desc' })
              .then((r) => r.data)
              .catch(() => [])
          )
        );
      })
      .then((results) => {
        setAllReports(results.flat());
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const reloadReports = useCallback(() => {
    if (orgs.length === 0) return;
    setLoading(true);
    Promise.all(
      orgs.map((o) =>
        getReportsByOrg(o.id, { page: 0, size: 10000, sortBy: 'assessmentDate', sortDir: 'desc' })
          .then((r) => r.data)
          .catch(() => [])
      )
    )
      .then((results) => setAllReports(results.flat()))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgs]);

  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const orgDropdownRef = useRef(null);

  // Close org dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target)) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOrg = (orgId) => {
    setSelectedOrgIds((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
    setPage(0);
  };

  // Sort comparator
  const getSortValue = (r, key) => {
    if (key === 'assessmentDate') return r.assessmentDate || '';
    if (key === 'updatedAt') return r.updatedAt || '';
    if (key === 'systemVersion') return r.systemVersion || 0;
    return (r[key] || '').toString().toLowerCase();
  };

  // Filter + sort + paginate
  const filtered = useMemo(() => {
    let result = allReports;

    // Org filter
    if (selectedOrgIds.size < orgs.length) {
      result = result.filter((r) => selectedOrgIds.has(r.organisationId));
    }

    // Status filter
    if (statusFilter) {
      result = result.filter((r) => r.status === statusFilter);
    }

    // Text search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((r) =>
        (r.siteName || '').toLowerCase().includes(q)
        || (r.assessorName || '').toLowerCase().includes(q)
        || (r.organisationName || '').toLowerCase().includes(q)
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      const aVal = getSortValue(a, sortBy);
      const bVal = getSortValue(b, sortBy);
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [allReports, selectedOrgIds, orgs.length, statusFilter, search, sortBy, sortDir]);

  const totalCount = filtered.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortDir(columnKey === 'assessmentDate' || columnKey === 'updatedAt' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const handleDuplicateConfirm = async () => {
    if (!duplicateTarget) return;
    setDuplicating(true);
    try {
      const newReport = await duplicateReport(duplicateTarget.id);
      if (newReport && newReport.id && newReport.status !== 'DRAFT') {
        await updateReportStatus(newReport.id, 'DRAFT');
      }
      setDuplicateTarget(null);
      reloadReports();
    } catch (err) {
      alert(err.message);
    } finally {
      setDuplicating(false);
    }
  };

  const handleExportExcel = async (orgId) => {
    setExporting(true);
    try {
      await exportReportListExcel(orgId, { sortBy, sortDir });
    } catch (err) {
      alert('Failed to export: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const formatDateTime = (dt) => {
    if (!dt) return '\u2014';
    const d = new Date(dt);
    return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const statusBadge = (status) => {
    const colors = { DRAFT: '#6b7280', IN_PROGRESS: '#f59e0b', REVIEW: '#3b82f6', ASSESSOR_COMPLETED: '#10b981', QA_APPROVED: '#7c3aed' };
    const labels = { DRAFT: 'Draft', IN_PROGRESS: 'In Progress', REVIEW: 'Review', ASSESSOR_COMPLETED: 'Assessor Completed', QA_APPROVED: 'QA Approved' };
    return <span style={{ background: colors[status] || '#6b7280', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{labels[status] || status}</span>;
  };

  const sortIcon = (columnKey) => {
    if (sortBy !== columnKey) return <span style={{ color: '#ccc', marginLeft: 4 }}>{'\u2195'}</span>;
    return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>;
  };

  const startItem = totalCount > 0 ? page * PAGE_SIZE + 1 : 0;
  const endItem = Math.min((page + 1) * PAGE_SIZE, totalCount);

  // For export, only offer if exactly one org is selected
  const singleExportOrg = selectedOrgIds.size === 1 ? [...selectedOrgIds][0] : null;

  return (
    <div className="page-container">
      <div className="form-card">
        <Breadcrumb items={[
          { label: 'Dashboard', to: '/' },
          { label: 'Reports' },
        ]} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h2 style={{ margin: 0 }}>Reports</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {singleExportOrg && paged.length > 0 && (
              <button
                className="btn btn-outline"
                onClick={() => handleExportExcel(singleExportOrg)}
                disabled={exporting}
                style={{ fontSize: '0.85rem', color: '#333', borderColor: '#ccc' }}
              >
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </button>
            )}
            <Link to="/reports/new" className="btn btn-primary">New Report</Link>
          </div>
        </div>

        {/* Filters: Search + Organisation multiselect + Status */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <input
            type="text"
            placeholder="Filter by site, assessor, organisation..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            style={{ flex: 1, minWidth: 200, padding: '0.65rem 0.8rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.95rem' }}
          />

          {/* Organisation multi-select dropdown */}
          {orgs.length > 1 && (
            <div ref={orgDropdownRef} style={{ position: 'relative', minWidth: 200 }}>
              <button
                type="button"
                onClick={() => setOrgDropdownOpen((v) => !v)}
                style={{
                  width: '100%',
                  padding: '0.65rem 2.2rem 0.65rem 0.8rem',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  fontSize: '0.95rem',
                  background: '#fff',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: selectedOrgIds.size === orgs.length ? '#555' : '#333',
                  appearance: 'none',
                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23555' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.8rem center',
                }}
              >
                {selectedOrgIds.size === 0
                  ? 'No organisations'
                  : selectedOrgIds.size === orgs.length
                    ? 'All Organisations'
                    : `${selectedOrgIds.size} of ${orgs.length} organisations`}
              </button>
              {orgDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 100,
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: 6,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  marginTop: 4,
                  maxHeight: 240,
                  overflowY: 'auto',
                }}>
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                    <button
                      type="button"
                      onClick={() => { setSelectedOrgIds(new Set(orgs.map((o) => o.id))); setPage(0); }}
                      style={{ background: 'none', border: 'none', color: '#4361ee', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}
                    >
                      Select All
                    </button>
                    <span style={{ color: '#ccc' }}>|</span>
                    <button
                      type="button"
                      onClick={() => { setSelectedOrgIds(new Set()); setPage(0); }}
                      style={{ background: 'none', border: 'none', color: '#4361ee', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}
                    >
                      Clear All
                    </button>
                  </div>
                  {orgs.map((o) => (
                    <label
                      key={o.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f7fa'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedOrgIds.has(o.id)}
                        onChange={() => toggleOrg(o.id)}
                        style={{ width: 16, height: 16, accentColor: '#4361ee', cursor: 'pointer' }}
                      />
                      {o.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
              setSearchParams((prev) => {
                const p = new URLSearchParams(prev);
                if (e.target.value) p.set('status', e.target.value); else p.delete('status');
                return p;
              });
            }}
            style={{ padding: '0.65rem 0.8rem', border: '1px solid #ddd', borderRadius: 6, fontSize: '0.95rem', minWidth: 150 }}
          >
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>

        {loading && <p>Loading reports...</p>}

        {!loading && paged.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="reports-table-desktop">
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          onClick={col.sortable ? () => handleSort(col.key) : undefined}
                          style={col.sortable ? { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' } : { whiteSpace: 'nowrap' }}
                        >
                          {col.label}{col.sortable && sortIcon(col.key)}
                        </th>
                      ))}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((r) => (
                      <tr key={`${r.organisationId}-${r.id}`}>
                        <td style={{ fontSize: '0.85rem' }}>{r.organisationName || '\u2014'}</td>
                        <td>
                          {r.siteName || '\u2014'}
                          {r.siteName && r.siteName.startsWith('[COPY]') && (
                            <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', marginLeft: 6 }}>Copy</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{r.assessmentDate || '\u2014'}</td>
                        <td>{r.assessorName || '\u2014'}</td>
                        <td>{r.versionNumber || '\u2014'}</td>
                        <td>{r.systemVersion || '\u2014'}</td>
                        <td style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{formatDateTime(r.updatedAt)}</td>
                        <td>{statusBadge(r.status)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <Link to={`/reports/${r.id}/view`}>View</Link>
                          <Link to={`/reports/${r.id}`} style={{ marginLeft: 8 }}>Edit</Link>
                          <button className="btn btn-outline" style={{ marginLeft: 6, padding: '2px 8px', fontSize: '0.75rem' }} onClick={() => setDuplicateTarget(r)}>Duplicate</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="reports-cards-mobile">
              {paged.map((r) => (
                <div key={`m-${r.organisationId}-${r.id}`} className="report-card-mobile">
                  <div className="report-card-mobile-top">
                    <span className="report-card-mobile-site">
                      {r.siteName || '\u2014'}
                      {r.siteName && r.siteName.startsWith('[COPY]') && (
                        <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', marginLeft: 6 }}>Copy</span>
                      )}
                    </span>
                    {statusBadge(r.status)}
                  </div>
                  <div className="report-card-mobile-meta">
                    <span>{r.organisationName || '\u2014'}</span>
                    <span>{r.assessorName || '\u2014'}</span>
                  </div>
                  <div className="report-card-mobile-meta">
                    <span>{r.assessmentDate || '\u2014'}</span>
                    <span style={{ fontSize: '0.78rem', color: '#999' }}>{formatDateTime(r.updatedAt)}</span>
                  </div>
                  <div className="report-card-mobile-actions">
                    <Link to={`/reports/${r.id}/view`} className="btn btn-outline" style={{ flex: 1, textAlign: 'center', color: '#333', borderColor: '#ccc' }}>View</Link>
                    <Link to={`/reports/${r.id}`} className="btn btn-primary" style={{ flex: 1, textAlign: 'center' }}>Edit</Link>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="reports-pagination">
              <span>
                {totalPages > 1
                  ? `Showing ${startItem}\u2013${endItem} of ${totalCount} reports`
                  : `${totalCount} report${totalCount !== 1 ? 's' : ''}`}
              </span>
              {totalPages > 1 && (
                <div className="reports-pagination-buttons">
                  <button
                    className="btn btn-outline reports-pagination-desktop-only"
                    style={{ padding: '4px 12px', fontSize: '0.8rem', color: '#333', borderColor: '#ccc' }}
                    disabled={page === 0}
                    onClick={() => setPage(0)}
                  >
                    First
                  </button>
                  <button
                    className="btn btn-outline"
                    style={{ padding: '4px 12px', fontSize: '0.8rem', color: '#333', borderColor: '#ccc' }}
                    disabled={page === 0}
                    onClick={() => setPage(page - 1)}
                  >
                    Prev
                  </button>
                  <span style={{ padding: '4px 8px', lineHeight: '1.8' }}>
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline"
                    style={{ padding: '4px 12px', fontSize: '0.8rem', color: '#333', borderColor: '#ccc' }}
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                  <button
                    className="btn btn-outline reports-pagination-desktop-only"
                    style={{ padding: '4px 12px', fontSize: '0.8rem', color: '#333', borderColor: '#ccc' }}
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(totalPages - 1)}
                  >
                    Last
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {!loading && paged.length === 0 && <EmptyState message="No reports found." />}
      </div>

      {/* Duplicate confirmation modal */}
      {duplicateTarget && (
        <div className="location-modal-overlay" onClick={() => { if (!duplicating) setDuplicateTarget(null); }}>
          <div className="location-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="location-modal-header">
              <h3>Duplicate Report</h3>
              <button className="location-modal-close" onClick={() => { if (!duplicating) setDuplicateTarget(null); }}>&times;</button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: '#333' }}>
                Are you sure you want to duplicate this report?
              </p>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
                <div><strong>{duplicateTarget.siteName || 'Untitled'}</strong></div>
                {duplicateTarget.organisationName && <div style={{ color: '#666' }}>{duplicateTarget.organisationName}</div>}
                {duplicateTarget.assessorName && <div style={{ color: '#666' }}>Assessor: {duplicateTarget.assessorName}</div>}
              </div>
              <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#666' }}>
                The duplicated report will be set to <strong>Draft</strong> status.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-outline"
                  style={{ color: '#333', borderColor: '#ccc' }}
                  onClick={() => setDuplicateTarget(null)}
                  disabled={duplicating}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleDuplicateConfirm}
                  disabled={duplicating}
                >
                  {duplicating ? 'Duplicating...' : 'Duplicate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
